import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { MdTv } from "react-icons/md";
import { login } from "../api/auth";
import { setupApi } from "../api/superadmin";
import { useAuthStore } from "../store/authStore";
import ActionButton from "../components/shared/ActionButton";

export default function Login() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("tv_session_expired")) {
      setSessionExpired(true);
      sessionStorage.removeItem("tv_session_expired");
    }
  }, []);

  // Detecta si el sistema necesita seed inicial (no hay superadmin todavía)
  const { data: seedInfo } = useQuery({
    queryKey: ["needs-seed"],
    queryFn: setupApi.needsSeed,
    retry: false,
    staleTime: Infinity,
  });

  const seedMut = useMutation({
    mutationFn: setupApi.seed,
    onSuccess: (d) => {
      toast.success(`Admin creado: ${d.email}. Ahora puedes iniciar sesión.`);
    },
    onError: (e) => toast.error(e?.response?.data?.error || "No se pudo crear admin"),
  });

  const loginMut = useMutation({
    mutationFn: () => login(email, password),
    onSuccess: (data) => {
      setAuth(data);
      toast.success("Bienvenido");
      navigate(
        data.rol === "superadmin" ? "/superadmin/dashboard" : "/local/stream",
        { replace: true }
      );
    },
    onError: (e) => {
      const msg = e?.response?.data?.error || "No se pudo iniciar sesión";
      toast.error(msg);
    },
  });

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background:
          "linear-gradient(135deg, var(--navy-dk) 0%, var(--navy) 100%)",
      }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-teal/10 rounded-full mb-3">
            <MdTv size={30} className="text-teal" />
          </div>
          <h1 className="text-2xl font-bold text-navy">TV Central</h1>
          <p className="text-sm text-gray-500 mt-1">Sistema de TV Corporativa</p>
        </div>

        {sessionExpired && (
          <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
            Tu sesión expiró. Vuelve a iniciar sesión para continuar.
          </div>
        )}

        {seedInfo?.needs_seed && (
          <div className="mb-5 p-4 bg-warning/10 border border-warning/30 rounded-lg">
            <p className="text-sm font-semibold text-warning mb-1">
              Primer arranque detectado
            </p>
            <p className="text-xs text-gray-600 mb-3">
              No hay superadmin todavía. Crea el primero usando las credenciales
              guardadas en los secretos del Worker (ADMIN_INITIAL_EMAIL +
              ADMIN_INITIAL_PASSWORD).
            </p>
            <ActionButton
              variant="primary"
              className="w-full h-9 text-xs"
              onClick={() => seedMut.mutate()}
              disabled={seedMut.isPending}
            >
              {seedMut.isPending ? "Creando…" : "Crear primer superadmin"}
            </ActionButton>
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); loginMut.mutate(); }}
          className="space-y-4"
        >
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
              Email
            </label>
            <input
              type="email" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition"
              placeholder="tu@dominio.com"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
              Contraseña
            </label>
            <input
              type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition"
              placeholder="••••••••"
            />
          </div>
          <ActionButton
            type="submit" variant="primary"
            disabled={loginMut.isPending}
            className="w-full h-11"
          >
            {loginMut.isPending ? "Ingresando…" : "Ingresar al sistema"}
          </ActionButton>
        </form>

        <p className="text-center text-[11px] text-gray-400 mt-6">
          Servido por Cloudflare · Pages + Workers + D1 + R2
        </p>
      </div>
    </div>
  );
}
